import { api } from './apiService';
import insightsDemo from '../data/insightsDemo.json';

export type WorldIntensityCountry = {
  iso2: string;
  name?: string;
  articleCount: number;
  clusterCount: number;
  /** Backend-combined weight for choropleth (defaults to articleCount + clusterCount in demo). */
  total: number;
};

export type WorldIntensity24hResponse = {
  windowHours: number;
  generatedAt: string;
  countries: WorldIntensityCountry[];
};

export type TrendingKeyword = { term: string; score: number };
export type TrendingTopic = { label: string; mentions: number };

export type Trending24hResponse = {
  windowHours: number;
  generatedAt: string;
  keywords: TrendingKeyword[];
  topics: TrendingTopic[];
};

export type DevelopingFeedItem = {
  id: string;
  kind: 'cluster' | 'article';
  title: string;
  summary: string;
  countryIso2?: string;
  publishedAt: string;
  articleCount: number;
};

export type DevelopingFeedResponse = {
  generatedAt: string;
  items: DevelopingFeedItem[];
};

const demo = insightsDemo as {
  worldIntensity24h: WorldIntensity24hResponse;
  trending24h: Trending24hResponse;
  developingFeed: DevelopingFeedResponse;
};

/** Demo-only: same flag as `setupProxy` mock. When not "1", failed `/api/insights/*` calls propagate (real backend required). */
const insightsMockClientFallback =
  String(process.env.REACT_APP_INSIGHTS_MOCK ?? '').trim() === '1';

async function getInsights<T>(
  path: string,
  fallback: T,
  config?: { params?: Record<string, string> }
): Promise<T> {
  try {
    return (await api.get(path, config ?? {})) as T;
  } catch (e) {
    if (insightsMockClientFallback) return fallback;
    throw e;
  }
}

export function fetchWorldIntensity24h(): Promise<WorldIntensity24hResponse> {
  return getInsights('/api/insights/world-intensity-24h', demo.worldIntensity24h);
}

/** `clientId` should be the analytic profile id (maps to media-ingestion keyword tracker client_id). */
export function fetchTrending24h(clientId?: string | null): Promise<Trending24hResponse> {
  const trimmed = clientId?.trim();
  const params =
    trimmed && trimmed.length > 0 ? { clientId: trimmed } : undefined;
  return getInsights(
    '/api/insights/trending-24h',
    demo.trending24h,
    params ? { params } : undefined
  );
}

export function fetchDevelopingFeed(clientId?: string | null): Promise<DevelopingFeedResponse> {
  const trimmed = clientId?.trim();
  const params = trimmed && trimmed.length > 0 ? { clientId: trimmed } : undefined;
  return getInsights(
    '/api/insights/developing-feed',
    demo.developingFeed,
    params ? { params } : undefined
  );
}
